/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import moment from "moment";
import { api } from "core/api";
import { Card, Input, Table, SvgIcon, Button, Select, ControlledTable } from "core/components";
import { purchase_requests_admin_material_reordering_index_path } from "routes";
import ControlledTableContext from "core/components/ControlledTableContext";

const MaterialReorderingRequestHistory = ({ warehouses, hasPurchaseRequestHistory }): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [purchaseRequest, setPurchaseRequest] = React.useState(null);
  const [showPurchaseRequestModal, setShowPurchaseRequestModal] = React.useState(false);
  const [warehouseFilter, setWarehouseFilter] = React.useState("");
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: "code",
        Header: "Purchase Request",
        accessor: "code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Requestor",
        accessor: "requested_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Approver",
        accessor: "approved_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Closed",
        accessor: "date_closed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Purchase Request"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setPurchaseRequest(props.row.original);
                  setShowPurchaseRequestModal(!showPurchaseRequestModal);
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
            </div>
          );
        }
      }
    ],
    []
  );

  const purchaseRequestItemColumns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Inventory Item",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          return <p className="text-center text-bold">{props.value}</p>;
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return <p className="text-center text-bold">{props.value}</p>;
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        Cell: (props) => {
          return <p className="text-center text-bold">{props.value}</p>;
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const getPurchaseRequests = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(
        purchase_requests_admin_material_reordering_index_path({ history: true, page, per, filters: JSON.stringify(filters) }),
        { signal }
      );
      setPurchaseRequests(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No replenishment request history.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Purchase Request Number" onChange={(e) => onChange("code", e.target.value)} />
        </Col>
        <Col>
          <Select
            placeholder="Filter by Warehouse"
            options={warehouses}
            isSearchable={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isOptionDisabled={(option) => !option.active}
            onChange={(newOption) => {
              if (newOption) {
                setWarehouseFilter(newOption.id);
                table.current.setFilter("warehouse", newOption.id);
              } else {
                setWarehouseFilter("");
                table.current.setFilter("warehouse", "");
              }
            }}
            value={warehouses.find((warehouse) => warehouse.id == warehouseFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={purchaseRequests}
              ref={table}
              isLoading={isLoading}
              fetchData={getPurchaseRequests}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
              hiddenColumns={["warehouse"]}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return hasPurchaseRequestHistory ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Card.Body>{content()}</Card.Body>
      <Modal dialogClassName="modal-dialog-centered modal-lg" show={showPurchaseRequestModal}>
        <Modal.Header>{`Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>
          <Table columns={purchaseRequestItemColumns} data={purchaseRequest?.purchase_request_items} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseRequestModal(!showPurchaseRequestModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default MaterialReorderingRequestHistory;
